import React, { useState, useEffect, useId } from 'react';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ID, useAccordionContext } from './_AccordionContext';
import { navigationClicked } from '@repo/ui/src/services/analytics';

interface AccordionItemProps {
  children: React.ReactNode;
  heading: string;
  uuid?: ID;
  className?: string;
  headerClassName?: string;
  headerFontClass?: string;
  onClick?: (isOpen: boolean) => void;
}

export const AccordionItem = ({
  heading,
  uuid,
  children,
  className,
  headerClassName,
  headerFontClass = 'tra-text-web-heading-5',
}: AccordionItemProps) => {
  const _id = useId();
  const id = uuid ?? _id;
  const { openDrawer, previousOpenDrawer, toggleDrawer, dark, divider } = useAccordionContext();
  const isOpen = !!openDrawer?.includes(id);
  const previouslyOpen = !!previousOpenDrawer?.includes(id);
  const isOpening = isOpen && !previouslyOpen;
  const isClosing = !isOpen && previouslyOpen;
  const [isClosed, setIsClosed] = useState(!isOpen);
  const [finishedAnimation, setFinishedAnimation] = useState(true);

  useEffect(() => {
    if (isOpening) {
      const timer = setTimeout(() => {
        setIsClosed(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpening]);

  useEffect(() => setFinishedAnimation(false), [isOpening, isClosing]);

  const panelId = `accordion-panel-${id}-${useId()}`;
  const headingId = `accordion-heading-${id}-${useId()}`;

  return (
    <div className="text-neutral-700 group" role="presentation">
      <button
        id={headingId}
        role="tab"
        aria-controls={panelId}
        aria-selected={isOpen ? 'true' : 'false'}
        aria-expanded={isOpen ? 'true' : 'false'}
        suppressHydrationWarning
        className={classNames(
          'w-full text-left grid grid-flow-col justify-between py-6 cursor-pointer',
          'focus-visible:outline-brand-200 focus-visible:outline-[3px] focus-visible:outline',
          dark && 'text-white',
          headerClassName,
          headerFontClass,
        )}
        onClick={() => {
          toggleDrawer(id);
          navigationClicked({
            nav_type: 'Top Navigation',
            clicked_text: heading ?? '',
            primary_header: heading ?? '',
          });
        }}
      >
        {heading}
        <span className="w-6 self-stretch">
          <ChevronIcon direction={isOpen && !isClosed ? 'up' : 'down'} decorative />
        </span>
      </button>
      <div
        className={classNames(
          'grid transition-all duration-500 ease-in-out',
          !isClosed && isOpen ? 'grid-rows-[minmax(0,1fr)]' : 'grid-rows-[minmax(0,0fr)]',
        )}
        onTransitionEnd={() => {
          if (isClosing && !isClosed) {
            setIsClosed(true);
          }
          setFinishedAnimation(true);
        }}
      >
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={headingId}
          suppressHydrationWarning
          tabIndex={0}
          className={classNames(
            'my-6',
            divider && 'mt-0',
            (isClosed || (!finishedAnimation && (isOpening || isClosing))) && 'overflow-y-hidden',
            className,
          )}
        >
          {divider && <hr className="h-1 w-12 bg-brand-300 mb-5 border-0" />}
          {children}
        </div>
      </div>
    </div>
  );
};
